import { useRef } from "react";
import {
  getComponent,
  getLibraryComponents,
  getFlagSummary,
  addConnection,
  removeFromBoard,
  moveToSlot,
} from "../state/roomState";

export default function AuditWorkspace({
  room,
  pendingConnectionFrom,
  setPendingConnectionFrom,
  onPersist,
  onToast,
  onResetRoom,
  onSaveRoom,
}) {
  const draggedRef = useRef(null);

  if (!room) {
    return (
      <section className="panel section detail-card audit-workspace">
        <div className="section-head">
          <div>
            <div className="eyebrow">Room Audit</div>
            <h3>Select a room</h3>
            <p>Choose a room from the resolve list to inspect its current mock board, fix missing pieces, and save the rebuilt state.</p>
          </div>
          <div className="btn-row">
            <button className="btn btn-ghost">Reset room</button>
            <button className="btn btn-primary">Save room state</button>
          </div>
        </div>
        <div className="empty-state">
          Select a room below the calendar to begin the audit. Then drag parts from the library onto the board, click one placed item and then another to connect them, and use the checks on the right to confirm the room is complete.
        </div>
      </section>
    );
  }

  const { checks, failing } = getFlagSummary(room);
  const library = getLibraryComponents(room);

  function handleDrop(type, slotIndex) {
    if (!draggedRef.current) return;
    if (moveToSlot(room, draggedRef.current, type, slotIndex)) {
      onPersist();
      onToast("Placed on board");
    } else {
      onToast("Wrong slot type");
    }
    draggedRef.current = null;
  }

  function handleCardClick(componentId) {
    if (!pendingConnectionFrom) {
      setPendingConnectionFrom(componentId);
      return;
    }
    if (pendingConnectionFrom === componentId) {
      setPendingConnectionFrom(null);
      return;
    }
    const added = addConnection(room, pendingConnectionFrom, componentId);
    setPendingConnectionFrom(null);
    if (added) { onPersist(); onToast("Connection added"); }
  }

  function handleRemove(componentId) {
    const comp = getComponent(room, componentId);
    removeFromBoard(room, componentId);
    if (pendingConnectionFrom === componentId) setPendingConnectionFrom(null);
    onPersist();
    onToast((comp ? comp.name : "Item") + " removed");
  }

  function handleRemoveConnection(index) {
    room.connections.splice(index, 1);
    onPersist();
    onToast("Connection removed");
  }

  function handleNotesChange(e) {
    room.auditNotes = e.target.value;
    onPersist();
  }

  return (
    <section className="panel section detail-card audit-workspace">
      <div className="section-head">
        <div>
          <div className="eyebrow">Room Audit</div>
          <h3>{room.label || room.id}</h3>
          <p>{room.notes}</p>
        </div>
        <div className="btn-row">
          <button className="btn btn-ghost" onClick={onResetRoom}>Reset room</button>
          <button className="btn btn-primary" onClick={onSaveRoom}>Save room state</button>
        </div>
      </div>

      <div className="detail-grid">
        {/* Left — library + board */}
        <div className="detail-panel">
          <div className="room-summary">
            <div className="flag-row">
              <span className={"pill " + (failing.length ? "pill-bad" : "pill-good")}>{failing.length ? failing.length + " flags" : "Ready to save"}</span>
              <span className="pill pill-soft">{room.stage}</span>
              <span className="pill pill-soft">{room.weeklyEvents.length} weekly events</span>
            </div>
            <p>{room.reason}</p>
          </div>

          {/* Component Library */}
          <div className="library-card">
            <div className="section-head">
              <div>
                <div className="eyebrow">Component Library</div>
                <h3 style={{ fontSize: 24 }}>Drag parts onto the board</h3>
              </div>
              <div className="pill pill-soft">{library.length} unplaced</div>
            </div>
            <div className="library-list">
              {library.length === 0 ? (
                <div className="empty-state" style={{ padding: 20 }}>Everything is on the board. Use the checks to verify connections before saving.</div>
              ) : (
                library.map((comp) => (
                  <div
                    key={comp.id}
                    className="component-chip"
                    draggable
                    onDragStart={() => { draggedRef.current = comp.id; }}
                    onDragEnd={() => { draggedRef.current = null; }}
                  >
                    <strong>{comp.name}</strong>
                    <span>{comp.type} · drop into a {comp.spotType} slot</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Board */}
          <div className="library-card">
            <div className="section-head">
              <div>
                <div className="eyebrow">Board Rebuild</div>
                <h3 style={{ fontSize: 24 }}>Place and connect</h3>
              </div>
              <div className="pill pill-soft">{pendingConnectionFrom ? "Choose target item" : "Click any placed item to connect"}</div>
            </div>
            <div className="workspace-note">{pendingConnectionFrom ? "Connect mode is on. Click another placed item to create the link." : "Drag parts from the library into matching board spots. To connect, click one placed item, then click the item it should link to."}</div>
            <div className="board-shell">
              <div className="board">
                {["output", "sensor", "service", "virtual"].map((type) => (
                  <div className="board-row" key={type}>
                    <div className="board-label">
                      <strong>{type[0].toUpperCase() + type.slice(1)}s</strong>
                      <span>{type === "service" ? "Service with sensor must connect to the right sensor." : type === "virtual" ? "Virtuals should carry formula links." : "Place each item on a board spot."}</span>
                    </div>
                    {room.board[type].map((cid, si) => (
                      <div
                        key={si}
                        className="slot"
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("drag-over"); }}
                        onDragLeave={(e) => e.currentTarget.classList.remove("drag-over")}
                        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("drag-over"); handleDrop(type, si); }}
                      >
                        <div className="slot-title"><span>{type} spot {si + 1}</span><span>{cid ? "occupied" : "drop here"}</span></div>
                        {cid && (
                          <PlacedCard
                            room={room}
                            componentId={cid}
                            isSelected={pendingConnectionFrom === cid}
                            onClick={() => handleCardClick(cid)}
                            onRemove={() => handleRemove(cid)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right — connections, checks, notes */}
        <div className="detail-panel">
          <div className="library-card">
            <div className="section-head"><div><div className="eyebrow">Connections</div><h3 style={{ fontSize: 24 }}>Current links</h3></div></div>
            <div className="connection-list">
              {room.connections.length === 0 ? (
                <div className="empty-state" style={{ padding: 20 }}>No links yet. Click Connect on one placed item and then click Connect on the target item.</div>
              ) : (
                room.connections.map(([fromId, toId], i) => {
                  const from = getComponent(room, fromId);
                  const to = getComponent(room, toId);
                  return (
                    <div className="connection-item" key={i}>
                      <strong style={{ display: "block", color: "var(--ink)", marginBottom: 4 }}>{from ? from.name : fromId} → {to ? to.name : toId}</strong>
                      <button type="button" className="mini-btn danger" onClick={() => handleRemoveConnection(i)}>Remove link</button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="library-card">
            <div className="section-head"><div><div className="eyebrow">Checks</div><h3 style={{ fontSize: 24 }}>Audit flags</h3></div></div>
            <div className="check-list">
              {checks.map((chk, i) => (
                <div key={i} className={"check-card " + (chk.ok ? "good" : "bad")}>
                  <strong>{chk.title}</strong>
                  <p>{chk.detail}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="library-card">
            <div className="eyebrow">Room Notes</div>
            <div className="field-row">
              <textarea
                placeholder="Add audit notes for the current room..."
                defaultValue={room.auditNotes || ""}
                onChange={handleNotesChange}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PlacedCard({ room, componentId, isSelected, onClick, onRemove }) {
  const comp = getComponent(room, componentId);
  if (!comp) return null;
  return (
    <div className={"placed-card" + (isSelected ? " selected" : "")} onClick={onClick}>
      <h4>{comp.name}</h4>
      <p>{comp.type}{comp.type === "virtual" ? " · " + comp.formula : ""}</p>
      <div className="mini-actions">
        <span className="pill pill-soft">{isSelected ? "Link from here" : "Click to link"}</span>
        <button type="button" className="mini-btn danger" onClick={(e) => { e.stopPropagation(); onRemove(); }}>Remove</button>
      </div>
    </div>
  );
}
